import React, { useEffect, useMemo, useRef, useState } from "react";
import BloxIcon from "../../components/icons/BloxIcon.jsx";
import { fmtAbbr } from "../../components/terminal/format.js";

/* Treasury cockpit — composes the four chrome rows that sit above
   the three-column body: command bar + quick-actions + status spine
   + alerts rail. The NL command parser is heuristic-only on the FE
   for v1 (BACKEND_TERMINAL_SPEC §D.11 ships a server parser later).

   Hotkeys (m c d b l r s y) dispatch the same action as the inline
   buttons; `/` focuses the command bar; `?` opens the chord sheet. */

const HOTKEYS = {
  m: { id:"move",       label:"Move",     icon:"move",    accent:"#4A8CFF" },
  c: { id:"spot",       label:"Convert",  icon:"convert", accent:"#06B6D4" },
  d: { id:"deploy",     label:"Deploy",   icon:"deploy",  accent:"#A78BFA" },
  b: { id:"borrow",     label:"Borrow",   icon:"borrow",  accent:"#FF5F6B" },
  l: { id:"lend",       label:"Lend",     icon:"lend",    accent:"#FF5F6B" },
  r: { id:"repay",      label:"Repay",    icon:"repay",   accent:"#FF5F6B" },
  s: { id:"sweep",      label:"Sweep",    icon:"sweep",   accent:"#4A8CFF" },
  y: { id:"sweepyield", label:"Yield",    icon:"yield",   accent:"#A78BFA" },
};

const SUGGESTIONS = [
  "borrow 200k usdc against btc",
  "lend 500k usdc 30d",
  "move 1m usd us-op → eu-treasury swift",
  "convert 1m usd to eur",
  "sweep to yield 2.4m usd → t-bill 13w",
  "auto-yield rule above 500k",
  "deploy 500k to t-bill 13w",
  "sweep now",
  "forward hedge 2m eur 30d",
  "repay 100k usdc on L-2401",
];

function parseCommand(s) {
  const q = (s || "").trim().toLowerCase();
  if (!q) return null;
  const amtMatch = q.match(/(\d+(?:\.\d+)?)\s*([kmb])?/i);
  const amt = amtMatch ? `${amtMatch[1]}${(amtMatch[2] || "").toUpperCase()}` : "";
  const has = (...kws) => kws.some((k) => q.includes(k));
  if (has("borrow"))                      return { id:"borrow",     label:"Borrow against assets", accent:"#FF5F6B", amt };
  if (has("lend"))                        return { id:"lend",       label:"Lend assets",           accent:"#FF5F6B", amt };
  if (has("repay"))                       return { id:"repay",      label:"Repay loan",            accent:"#FF5F6B", amt };
  if (has("move", "transfer", "send"))    return { id:"move",       label:"Move funds",            accent:"#4A8CFF", amt };
  if (has("sweep") && has("yield"))       return { id:"sweepyield", label:"Sweep to yield",        accent:"#A78BFA", amt };
  if (has("sweep"))                       return { id:"sweep",      label:"Sweep now",             accent:"#4A8CFF" };
  if (has("convert", "swap", "fx ", "spot")) return { id:"spot",    label:"Spot convert",          accent:"#06B6D4", amt };
  if (has("forward", "hedge"))            return { id:"forward",    label:"Forward hedge",         accent:"#06B6D4", amt };
  if (has("deploy", "invest"))            return { id:"deploy",     label:"Deploy to strategy",    accent:"#A78BFA", amt };
  if (has("redeem", "exit"))              return { id:"redeem",     label:"Redeem position",       accent:"#A78BFA" };
  return null;
}

function CommandBar({ onAction }) {
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(false);
  const inputRef = useRef(null);
  const parsed = useMemo(() => parseCommand(q), [q]);
  const filtered = useMemo(() => {
    if (!q) return SUGGESTIONS.slice(0, 6);
    return SUGGESTIONS.filter((s) => s.toLowerCase().includes(q.toLowerCase())).slice(0, 6);
  }, [q]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !["INPUT","SELECT","TEXTAREA"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const submit = (s) => {
    const p = parseCommand(s || q);
    if (p) {
      onAction(p);
      setQ("");
      inputRef.current?.blur();
    }
  };

  return (
    <div style={{ position:"relative" }}>
      <div style={{
        display:"flex", alignItems:"center", gap:0,
        borderTop:"1px solid var(--term-line)", borderBottom:"1px solid var(--term-line)",
        background: focus ? "var(--term-bg-pane-2)" : "var(--term-bg-pane)",
        transition:"background .15s",
      }}>
        <div style={{ padding:"0 14px", display:"flex", alignItems:"center", gap:8, borderRight:"1px solid var(--term-line)", height:38 }}>
          <span className="term-live-dot" style={{ width:6, height:6 }} />
          <span style={{ fontSize:9.5, letterSpacing:"0.18em", color:"var(--term-ink-2)", fontWeight:600 }}>CMD</span>
        </div>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocus(true)}
          onBlur={() => setTimeout(() => setFocus(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") { setQ(""); e.currentTarget.blur(); }
          }}
          placeholder="type to act — borrow · move · convert · deploy · lend · sweep …    ( press / to focus )"
          style={{
            flex:1, padding:"0 14px", height:38,
            background:"transparent", border:"none", color:"var(--term-ink-1)",
            fontFamily:"JetBrains Mono", fontSize:12.5, outline:"none", letterSpacing:"0.01em",
          }}
        />
        {parsed && (
          <div style={{ padding:"0 12px", borderLeft:"1px solid var(--term-line)", height:38, display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:5, height:5, background: parsed.accent }} />
            <span style={{ fontSize:11, color: parsed.accent, fontWeight:600 }}>{parsed.label}</span>
            {parsed.amt && <span className="mono" style={{ fontSize:11, color:"var(--term-ink-3)" }}>{parsed.amt}</span>}
            <span style={{ fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.12em" }}>↵ EXECUTE</span>
          </div>
        )}
      </div>

      {focus && filtered.length > 0 && (
        <div style={{
          position:"absolute", top:"100%", left:80, right:0, zIndex:50,
          background:"var(--term-bg-pane)", border:"1px solid var(--term-line)", borderTop:"none",
          boxShadow:"0 8px 24px rgba(0,0,0,0.4)",
        }}>
          {filtered.map((s, i) => {
            const p = parseCommand(s);
            return (
              <div
                key={i}
                onMouseDown={(e) => { e.preventDefault(); submit(s); }}
                className="mono"
                style={{
                  padding:"8px 14px", fontSize:11.5,
                  display:"flex", alignItems:"center", gap:10, cursor:"pointer",
                  borderBottom: i < filtered.length - 1 ? "1px solid var(--term-line-dim)" : "none",
                  color:"var(--term-ink-2)",
                }}
              >
                {p && <span style={{ width:5, height:5, background:p.accent, flexShrink:0 }} />}
                <span style={{ flex:1 }}>{s}</span>
                {p && <span style={{ fontSize:10, color:"var(--term-ink-3)" }}>{p.label}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Cell({ label, value, sub, color, onClick }) {
  const interactive = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      style={{
        padding:"9px 16px", background:"transparent", border:"none",
        borderRight:"1px solid var(--term-line)",
        display:"flex", flexDirection:"column", gap:2, minWidth:0, flexShrink:0,
        cursor: interactive ? "pointer" : "default",
        textAlign:"left", fontFamily:"inherit",
        transition:"background .12s",
      }}
      onMouseEnter={(e) => { if (interactive) e.currentTarget.style.background = "var(--term-bg-pane-2)"; }}
      onMouseLeave={(e) => { if (interactive) e.currentTarget.style.background = "transparent"; }}
    >
      <div style={{ fontSize:9, letterSpacing:"0.16em", color:"var(--term-ink-3)", fontWeight:600, textTransform:"uppercase" }}>{label}</div>
      <div className="mono" style={{ fontSize:14, color: color || "var(--term-ink-1)", letterSpacing:"-0.01em" }}>{value}</div>
      {sub && <div className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)" }}>{sub}</div>}
    </button>
  );
}

function StatusSpine({ state, onOpenApprovals, onOpenBreaches }) {
  const flow = parseFloat(state.net_flow_24h_usd) || 0;
  const fxPnl = parseFloat(state.fx_pnl_24h_usd) || 0;
  return (
    <div style={{ display:"flex", alignItems:"stretch", overflowX:"auto", borderBottom:"1px solid var(--term-line)", background:"var(--term-bg-pane)" }}>
      <Cell label="Group AUM" value={fmtAbbr(state.group_aum_usd)} sub="USD-eq · live" />
      <Cell label="Net flow 24h" value={`${flow >= 0 ? "+" : "−"}${fmtAbbr(Math.abs(flow))}`} color="var(--term-green)" sub="settled + in-flight" />
      <Cell label="In-flight" value={state.in_flight_count} sub="rails active" />
      <Cell label="Pending approvals" value={state.pending_approvals} color="var(--term-amber)" sub="click to triage" onClick={onOpenApprovals} />
      <Cell label="Breaches" value={state.active_breaches} color={state.active_breaches > 0 ? "var(--term-red)" : "var(--term-ink-1)"} sub={state.active_breaches > 0 ? "click to triage" : "none"} onClick={state.active_breaches > 0 ? onOpenBreaches : undefined} />
      <Cell label="FX P&L 24h" value={fmtAbbr(fxPnl)} color={fxPnl >= 0 ? "var(--term-green)" : "var(--term-red)"} sub="EUR/USD slip" />
      <Cell label="Next sweep" value={`${Math.floor(state.next_sweep_in_sec/60)}:${String(state.next_sweep_in_sec%60).padStart(2,"0")}`} sub="auto · all entities" />
      <Cell label="Heartbeat" value={`${state.heartbeat_sec.toFixed(1)}s`} color="var(--term-green)" sub="all rails ✓" />
      <div style={{ flex:1, minWidth:30 }} />
    </div>
  );
}

function QuickActions({ onAction }) {
  return (
    <div style={{ display:"flex", alignItems:"stretch", borderBottom:"1px solid var(--term-line)", background:"var(--term-bg-pane)" }}>
      <div style={{ padding:"0 14px", display:"flex", alignItems:"center", gap:8, borderRight:"1px solid var(--term-line)", flexShrink:0, height:42 }}>
        <span style={{ fontSize:9.5, letterSpacing:"0.18em", color:"var(--term-ink-2)", fontWeight:600 }}>QUICK ACTIONS</span>
      </div>
      {Object.entries(HOTKEYS).map(([k, a]) => (
        <button
          key={k}
          onClick={() => onAction(a)}
          style={{
            flex:1, minWidth:0, padding:"0 12px", height:42, background:"transparent", border:"none",
            borderRight:"1px solid var(--term-line-dim)", color:"var(--term-ink-1)", cursor:"pointer",
            fontFamily:"inherit", fontSize:12, fontWeight:600, letterSpacing:"0.02em",
            display:"flex", alignItems:"center", justifyContent:"flex-start", gap:10,
            transition:"background .12s, color .12s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = a.accent + "18"; e.currentTarget.style.color = a.accent; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--term-ink-1)"; }}
        >
          <span style={{ color:a.accent, display:"flex", flexShrink:0 }}><BloxIcon name={a.icon} size={14} /></span>
          <span style={{ whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{a.label}</span>
          <kbd className="mono" style={{ marginLeft:"auto", fontSize:9.5, color:"var(--term-ink-3)", border:"1px solid var(--term-line)", padding:"1px 5px", background:"var(--term-bg-base)", textTransform:"uppercase", flexShrink:0 }}>{k}</kbd>
        </button>
      ))}
    </div>
  );
}

function AlertsRail({ alerts, onAction }) {
  const sev = { high:"#FF5F6B", med:"#F5A524", low:"#4A8CFF" };
  return (
    <div style={{ display:"flex", alignItems:"stretch", borderBottom:"1px solid var(--term-line)", background:"var(--term-bg-pane-2)", overflowX:"auto" }}>
      <div style={{ padding:"0 14px", display:"flex", alignItems:"center", gap:8, borderRight:"1px solid var(--term-line)", flexShrink:0 }}>
        <span style={{ width:6, height:6, background:"#FF5F6B", boxShadow:"0 0 8px #FF5F6B", animation:"term-live-pulse 1.4s ease-in-out infinite" }} />
        <span style={{ fontSize:9.5, letterSpacing:"0.18em", color:"var(--term-ink-2)", fontWeight:600 }}>ALERTS · {alerts.length}</span>
      </div>
      {alerts.map((a) => (
        <div key={a.id} style={{ padding:"0 14px", height:32, display:"flex", alignItems:"center", gap:10, borderRight:"1px solid var(--term-line-dim)", flexShrink:0, fontSize:11 }}>
          <span style={{ width:5, height:5, background:sev[a.severity] }} />
          <span className="mono" style={{ color:"var(--term-ink-1)" }}>{a.text}</span>
          <button
            onClick={() => onAction({ id:a.action_id, label:a.action_label, accent:sev[a.severity] })}
            style={{
              padding:"3px 9px", fontSize:10, fontFamily:"inherit", letterSpacing:"0.06em",
              background: sev[a.severity], color:"#000", border:"none", cursor:"pointer", fontWeight:600,
            }}
          >
            {a.action_label.toUpperCase()} →
          </button>
        </div>
      ))}
    </div>
  );
}

export default function Cockpit({ state, onAction, onOpenApprovals, onOpenBreaches }) {
  // global hotkeys for action chords + 'a' to open the approvals queue
  useEffect(() => {
    const h = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "a") { e.preventDefault(); onOpenApprovals?.(); return; }
      if (HOTKEYS[k]) {
        e.preventDefault();
        onAction(HOTKEYS[k]);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onAction, onOpenApprovals]);

  return (
    <>
      <CommandBar onAction={onAction} />
      <QuickActions onAction={onAction} />
      <StatusSpine state={state} onOpenApprovals={onOpenApprovals} onOpenBreaches={onOpenBreaches} />
      <AlertsRail alerts={state.alerts} onAction={onAction} />
    </>
  );
}
